import { Component, EventEmitter, Input, OnInit, Output, ViewChild } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import {
    addDays,
    endOfDay,
    endOfMonth,
    endOfQuarter,
    format,
    parse,
    setDate,
    setMinutes,
    setMonth,
    startOfDay,
    startOfMonth,
    startOfQuarter,
    startOfWeek,
    startOfYear,
    subMonths,
    subQuarters,
    subWeeks,
    subYears,
} from 'date-fns';
import { DaterangepickerDirective } from 'ngx-daterangepicker-material';
import { Subject, tap } from 'rxjs';
import { DateFormat, ESmartDateSelectionRangeType as ERangeType } from './smart-date-selection.enum';
import {
    ISmartDateSelectionOutputRange,
    MomentDateRange,
    MomentDateRangeWithLabel,
} from './smart-date-selection.interface';

/**
 * Faithful date-fns replication of moment's locale-aware `weekday(n)`.
 *
 * moment's `weekday()` here uses a Sunday-start index (Sun=0, Mon=1, ... Sat=6),
 * so `moment(d).weekday(1)` is "the Monday of the Sunday-starting week containing d".
 * For Mon-Sat that Monday is in the same Mon-Sun calendar week; on a SUNDAY moment's
 * week has already rolled over, so `weekday(1)` is the *next* Monday. date-fns'
 * `startOfWeek(d, { weekStartsOn: 1 })` does NOT reproduce this (it would return the
 * preceding Monday on a Sunday), which silently shifts "This week"/"Last week" reports
 * by a full week whenever the current day is Sunday. Anchoring on a Sunday-start week
 * and offsetting by `n` reproduces moment exactly (verified across the full Mon-Sun
 * cycle, two-week lookbacks, and year boundaries).
 */
export function momentWeekday(d: Date, n: number): Date {
    return addDays(startOfWeek(d, { weekStartsOn: 0 }), n);
}

export function buildRanges(now: Date = new Date()): { [key in ERangeType]: [Date, Date] } {
    return {
        [ERangeType.CurrentWeek]: [startOfDay(momentWeekday(now, 1)), endOfDay(momentWeekday(now, 7))],
        [ERangeType.CurrentMonth]: [startOfMonth(now), endOfMonth(now)],
        [ERangeType.CurrentQuarter]: [startOfQuarter(now), endOfQuarter(now)],
        [ERangeType.CurrentFinancialYear]: [
            startOfDay(setDate(setMonth(subYears(now, 1), 3), 1)),
            endOfDay(setDate(setMonth(now, 2), 31)),
        ],
        [ERangeType.LastWeek]: [
            startOfDay(momentWeekday(subWeeks(now, 1), 1)),
            endOfDay(momentWeekday(subWeeks(now, 1), 7)),
        ],
        [ERangeType.LastMonth]: [startOfMonth(subMonths(now, 1)), endOfMonth(subMonths(now, 1))],
        [ERangeType.LastQuarter]: [startOfQuarter(subQuarters(now, 1)), endOfQuarter(subQuarters(now, 1))],
        [ERangeType.LastFinancialYear]: [
            startOfDay(setDate(setMonth(subYears(now, 2), 3), 1)),
            endOfDay(setDate(setMonth(subYears(now, 1), 2), 31)),
        ],
        [ERangeType.MonthToDate]: [startOfMonth(now), endOfDay(now)],
        [ERangeType.QuarterToDate]: [setMinutes(startOfQuarter(now), 2), endOfDay(now)],
        [ERangeType.YearToDate]: [startOfYear(now), endOfDay(now)],
    };
}

export const RANGES = buildRanges();
@UntilDestroy()
@Component({
    selector: 'app-smart-date-selection',
    templateUrl: './smart-date-selection.component.html',
    styleUrls: ['./smart-date-selection.component.scss'],
})
export class SmartDateSelectionComponent implements OnInit {
    @Input() rangeType: ERangeType = ERangeType.CurrentMonth;
    @Input() oneInputField = false;
    @Input() setInitRange = true;
    @Input() clearable = false;
    @Output()
    dateRangeSelected = new EventEmitter<ISmartDateSelectionOutputRange>();
    @ViewChild(DaterangepickerDirective, { static: true })
    pickerDirective: DaterangepickerDirective;

    public reset$: Subject<void> = new Subject();

    ranges = RANGES;
    selectedRange: MomentDateRange;
    form: FormGroup;
    oneInputControl = new FormControl('');
    rangeLabel: string;

    constructor(private fb: FormBuilder) {}

    ngOnInit(): void {
        this.initForm();
        this.initFormSubscriptions();
        if (this.setInitRange) this.setRange(this.rangeType);
        this.setResetListener();
    }

    public setDates(startDate: string, endDate: string) {
        this.patchForm({ startDate: new Date(startDate), endDate: new Date(endDate) });
        this.form.patchValue({ startDate, endDate });
    }

    private initForm(): void {
        this.form = this.fb.group({
            startDate: ['', Validators.required],
            endDate: ['', Validators.required],
            rangeLabel: [''],
        });
    }

    private initFormSubscriptions(): void {
        this.form.valueChanges
            .pipe(
                tap((values: MomentDateRange) => {
                    this.selectedRange = {
                        startDate: values.startDate
                            ? parse(values.startDate as unknown as string, DateFormat.yyyyMMdd, new Date())
                            : undefined,
                        endDate: values.endDate
                            ? parse(values.endDate as unknown as string, DateFormat.yyyyMMdd, new Date())
                            : undefined,
                    };
                    this.dateRangeSelected.emit({
                        startDate: this.selectedRange.startDate
                            ? format(this.selectedRange.startDate, DateFormat.yyyyMMdd)
                            : undefined,
                        endDate: this.selectedRange.endDate
                            ? format(this.selectedRange.endDate, DateFormat.yyyyMMdd)
                            : undefined,
                        rangeLabel: values.rangeLabel,
                    });
                }),
                untilDestroyed(this)
            )
            .subscribe();
    }

    private patchForm(dates: MomentDateRange): void {
        this.form.patchValue({
            startDate: dates.startDate ? format(dates.startDate, DateFormat.yyyyMMdd) : undefined,
            endDate: dates.startDate ? format(dates.endDate, DateFormat.yyyyMMdd) : undefined,
            rangeLabel: dates.rangeLabel,
        });

        if (dates.startDate && dates.endDate) {
            const datesRange =
                format(dates.startDate, DateFormat.yyyyMMdd) + ' to ' + format(dates.endDate, DateFormat.yyyyMMdd);
            this.oneInputControl.setValue(datesRange);
        } else {
            this.oneInputControl.setValue(null);
        }
    }

    datesUpdated({ startDate, endDate }: MomentDateRange): void {
        if (!startDate || !endDate) return;
        // rangeClicked Event got fired before datesUpdated event.
        if (this.rangeLabel) {
            this.rangeLabel = '';
        } else {
            this.patchForm({ startDate, endDate });
        }
    }

    rangeClicked(event: MomentDateRangeWithLabel): void {
        if (!event.dates[0] || !event.dates[1]) return;
        this.rangeLabel = event.label;
        this.patchForm({ startDate: event.dates[0], endDate: event.dates[1], rangeLabel: event.label });
    }

    clearDate() {
        this.patchForm({ startDate: null, endDate: null });
    }

    private setRange(datePairKey: ERangeType) {
        const [startDate, endDate] = this.ranges[datePairKey];
        this.patchForm({ startDate, endDate, rangeLabel: datePairKey });
    }

    private setResetListener() {
        this.reset$.pipe(untilDestroyed(this)).subscribe(() => {
            this.patchForm({ startDate: null, endDate: null });
        });
    }
}
